import { api } from '@/core/plugins/axios';
import { ApiResponse } from "@/shared/types/api";

import { Household } from '@/features/household/models/household';
import { HouseholdDTO } from '@/features/household/dto/household_dto';
import {
    CreateHouseholdForm,
    UpdateHouseholdNameForm,
    UpdateHouseholdSettingsForm
} from '@/features/household/form/household_form';
import {
    fromHouseholdDTO,
    toCreateHouseholdDTO,
    toUpdateHouseholdNameDTO,
    toUpdateHouseholdSettingsDTO
} from '@/features/household/mappers/household_mapper';

const BASE_URL = 'api/v1/households';

async function getHousehold(): Promise<ApiResponse<Household>> {
    try {
        const response = await api.get<HouseholdDTO>(`${BASE_URL}`);
    
        const domainHousehold = fromHouseholdDTO(response.data);
    
        return {
            status: 'success',
            data: domainHousehold
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Unknown error'
            }
        };
    }
}

async function createHousehold(form: CreateHouseholdForm): Promise<ApiResponse<Household>> {
    try {
        const dto = toCreateHouseholdDTO(form);
        const response = await api.post<HouseholdDTO>(`${BASE_URL}`, dto);

        if (response.status === 204) {
            return {
                status: 'error',
                error: {
                    code: 204,
                    message: 'No content returned from server',
                }
            };
        }
      
        const createdHousehold = fromHouseholdDTO(response.data);
        return {
            status: 'success',
            data: createdHousehold
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Failed to create household',
            }
        };
    }
}

async function updateHouseholdName(form: UpdateHouseholdNameForm): Promise<ApiResponse<Household>> {
    try {
        const dto = toUpdateHouseholdNameDTO(form);
        const response = await api.put<HouseholdDTO>(`${BASE_URL}/name`, dto);

        if (response.status === 204) {
            return {
                status: 'error',
                error: {
                    code: 204,
                    message: 'No content returned from server',
                }
            };
        }
        
        const updatedHousehold = fromHouseholdDTO(response.data);
        return {
            status: 'success',
            data: updatedHousehold
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Failed to update household',
            }
        };
    }
}

async function updateHouseholdSettings(form: UpdateHouseholdSettingsForm): Promise<ApiResponse<Household>> {
    try {
        const dto = toUpdateHouseholdSettingsDTO(form);
        const response = await api.put<HouseholdDTO>(`${BASE_URL}/settings`, dto);

        if (response.status === 204) {
            return {
                status: 'error',
                error: {
                    code: 204,
                    message: 'No content returned from server',
                }
            };
        }
        
        const updatedHousehold = fromHouseholdDTO(response.data);
        return {
            status: 'success',
            data: updatedHousehold
        };
    } catch (error: any) {
        return {
            status: 'error',
            error: {
                code: error.response?.status ?? 500,
                message: error.message ?? 'Failed to update household settings',
            }
        };
    }
}

export default {
    getHousehold,
    createHousehold,
    updateHouseholdName,
    updateHouseholdSettings
}
